import { z } from 'zod';
import { brandNameProblem, normalizeBrandName } from '../utils/brandName';

// Mirrors AWS Cognito's default password policy (8+ chars, upper, lower, number, special)
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(
    /[^a-zA-Z0-9]/,
    'Password must contain at least one special character'
  );

// Registration form validation
export const registerSchema = z
  .object({
    // Sent to Cognito as the standard `name` attribute; the sign-up trigger
    // stores it as fullName, which public pages show until a displayName is set.
    brandName: z.string().superRefine((value, ctx) => {
      const problem = brandNameProblem(normalizeBrandName(value));
      if (problem) ctx.addIssue({ code: 'custom', message: problem });
    }),
    email: z.string().email('Please enter a valid email address'),
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type RegisterFormData = z.infer<typeof registerSchema>;

// Sub-user first-login password setup
export const setNewPasswordSchema = z
  .object({
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine(data => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type SetNewPasswordFormData = z.infer<typeof setNewPasswordSchema>;

// Password reset with the emailed code: the same password rules as sign-up
export const resetPasswordSchema = z
  .object({
    email: z.string().email('Please enter a valid email address'),
    code: z.string().trim().min(1, 'Enter the code from your email'),
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine(data => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

// Email verification code (exactly 6 characters)
export const verifyEmailSchema = z.object({
  code: z
    .string()
    .min(6, 'Verification code must be at least 6 characters')
    .max(6, 'Verification code must be 6 characters'),
});

export type VerifyEmailFormData = z.infer<typeof verifyEmailSchema>;

// Which password requirements are met
export interface PasswordRequirements {
  minLength: boolean;
  hasLowercase: boolean;
  hasUppercase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
}

// Strength evaluation result shown in the password UI
export interface PasswordStrengthResult {
  score: number; // 0-4
  feedback: string[];
  requirements: PasswordRequirements;
}

// Scores a password 0-4 and lists the unmet requirements as feedback
export function getPasswordStrength(password: string): PasswordStrengthResult {
  const requirements: PasswordRequirements = {
    minLength: password.length >= 8,
    hasLowercase: /[a-z]/.test(password),
    hasUppercase: /[A-Z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecialChar: /[^a-zA-Z0-9]/.test(password),
  };

  const requirementsMet = Object.values(requirements).filter(Boolean).length;
  const score = Math.min(requirementsMet - 1, 4); // 0-4 scale

  const feedback: string[] = [];
  if (!requirements.minLength) {
    feedback.push('At least 8 characters');
  }
  if (!requirements.hasLowercase) {
    feedback.push('One lowercase letter');
  }
  if (!requirements.hasUppercase) {
    feedback.push('One uppercase letter');
  }
  if (!requirements.hasNumber) {
    feedback.push('One number');
  }
  if (!requirements.hasSpecialChar) {
    feedback.push('One special character');
  }

  return {
    score: Math.max(score, 0), // Ensure score is never negative
    feedback,
    requirements,
  };
}

// Maps a strength score to its display label and color class
export function getPasswordStrengthLabel(score: number): {
  label: string;
  colorClass: string;
} {
  switch (score) {
    case 0:
      return { label: 'Very Weak', colorClass: 'text-red-600' };
    case 1:
      return { label: 'Weak', colorClass: 'text-orange-600' };
    case 2:
      return { label: 'Fair', colorClass: 'text-yellow-600' };
    case 3:
      return { label: 'Good', colorClass: 'text-blue-600' };
    case 4:
      return { label: 'Strong', colorClass: 'text-green-600' };
    default:
      return { label: 'Unknown', colorClass: 'text-gray-600' };
  }
}
